//--------------------------------------------------------------------//
//******************** trunkDebugTab ************************************//
//--------------------------------------------------------------------//
// Places a real test call through the trunk and shows the live raw SIP
// trace (INVITE, 401, 403, ...) as it streams in - the self-service
// replacement for SSH'ing in and driving fs_cli by hand (see
// sip-gudhub-server/docs/sip-trunks/debug-tab.md). Structurally mirrors
// trunk-short-code.tab.js; the one real difference is the backend call
// streams instead of returning a single buffered response - see
// SipTrunksService.testCall's own comment.
//
// From/To are both explicit, user-controlled fields rather than this tab
// guessing a caller ID on the user's behalf - a real Twilio trunk rejects a
// test call with no caller ID (403, X-Twilio-Error 32204), and the point of
// this tool is to show that rejection clearly (see notableHeaders below), not
// to silently work around it.
import trunkDebugTemplateUrl from './trunk-debug.template.html';

angular.module('sipTrunksTableModule')

.directive('trunkDebugTab', ['SipTrunksService', 'GhDialog', '$timeout', function (SipTrunksService, GhDialog, $timeout) {
    return {
        restrict: 'E',
        scope: {
            trunk: '=',
            onUpdate: '&'
        },
        // Keeps the scrollable trace pane pinned to the newest line as
        // events stream in - without this, `.trunk-debug__trace`'s own
        // max-height/overflow-y:auto (sipTrunks.scss) just quietly grows a
        // scrollbar with the latest lines (the actual response, e.g. the
        // final "200 OK"/"403 ...") sitting below the fold, easy to miss
        // since the "Result" row below the trace pane makes it look like
        // the trace itself already ended.
        link: function (scope, element) {
            scope.$watch('trace.length', function (newLength, oldLength) {
                if (newLength === oldLength) return;
                $timeout(function () {
                    const traceEl = element[0].querySelector('.trunk-debug__trace');
                    if (traceEl) traceEl.scrollTop = traceEl.scrollHeight;
                });
            });
        },
        controller: ['$scope', 'localizationService', function ($scope, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            // Default the From dropdown to the trunk's first DID (whether
            // it has one or several) rather than the blank "trunk default"
            // option - a specific number is almost always what someone
            // testing this trunk actually wants selected already.
            $scope.from = ($scope.trunk.did_numbers && $scope.trunk.did_numbers[0] && $scope.trunk.did_numbers[0].number) || '';
            $scope.to = '';

            $scope.testing = false;
            $scope.trace = [];
            $scope.result = null;

            const safeApply = function () {
                $scope.$applyAsync();
            };

            function showError(message) {
                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="delete_sip_trunk-popup">
                                <div class="dialog-share-group">
                                    <p class="dialog-share-group__title">Error</p>
                                </div>
                                <div class="dialog-share-group__content">
                                    <p class="info">{{errorMessage}}</p>
                                    <div class="dialog-share-group__content__buttons">
                                        <button class="btn btn-blue" ng-click="cancel()">OK</button>
                                    </div>
                                </div>
                            </div>
                        `
                    },
                    locals: { errorMessage: message },
                    controller: ['$scope', 'errorMessage', function ($scope, errorMessage) {
                        $scope.errorMessage = errorMessage;
                    }]
                });
            }

            // Only headers actually useful for diagnosing a rejection or
            // an auth failure are called out in the trace pane - the rest
            // stay in the entry's own raw text (see the template's title
            // attribute). Auth headers included so a 401/407 challenge and
            // the digest response sent back are both visible at a glance,
            // not just on expand/copy.
            const NOTABLE_HEADER_PATTERN = /^(x-.*-error|reason|(proxy-|www-)?authenticate|(proxy-)?authorization)$/i;
            $scope.notableHeaders = function (headers) {
                return Object.keys(headers || {}).filter(key => NOTABLE_HEADER_PATTERN.test(key));
            };

            // Returns a plain array of class-name strings for ng-class -
            // NOT string-concatenated (`array + string` silently coerces
            // the array via toString(), joining class names with commas
            // into one invalid selector and quietly dropping every color
            // rule that depended on them - confirmed live, was the cause of
            // send/receive colors disappearing after --expanded was added).
            $scope.entryClasses = function (entry) {
                const classes = entry.type === 'sip'
                    ? ['trunk-debug__trace-entry--' + entry.direction]
                    : ['trunk-debug__trace-entry--log'];

                if (entry.type === 'sip' && entry.direction === 'recv') {
                    const match = (entry.startLine || '').match(/^SIP\/2\.0\s+(\d)\d\d/);
                    if (match) classes.push(`trunk-debug__trace-entry--status-${match[1]}xx`);
                }

                if (entry.expanded) classes.push('trunk-debug__trace-entry--expanded');

                return classes;
            };

            // Click-to-expand instead of relying on the title attribute's
            // native hover tooltip (easy to miss, awkward to read/copy from)
            // - toggled on the same entry object pushed into $scope.trace,
            // so ng-repeat re-renders it in place without extra tracking state.
            $scope.toggleEntry = function (entry) {
                entry.expanded = !entry.expanded;
            };

            // Plain-text rendering of the whole run (From/To, every trace
            // entry, the final result) - what copyTrace puts on the
            // clipboard, so a developer can paste the full exchange
            // straight into a ticket/chat instead of a screenshot. Uses
            // each entry's full raw text (the same thing shown when you
            // click to expand it), not just the summary startLine/notable
            // headers - the point of copying is to hand someone the whole
            // exchange, not a shorter version of what they can already see.
            function traceEntryText(entry) {
                if (entry.type === 'log') return entry.text.trim();
                return `${entry.direction === 'send' ? 'SENT' : 'RECEIVED'}:\n${entry.raw.trim()}`;
            }

            function buildTraceText() {
                const headerLines = $scope.lastTestType === 'register'
                    ? [`SIP trunk debug (register): ${$scope.trunk.name}`]
                    : [
                        `SIP trunk debug: ${$scope.trunk.name}`,
                        `From: ${$scope.from || '(none)'}`,
                        `To: ${$scope.to}`
                    ];
                const lines = [
                    ...headerLines,
                    '',
                    '--- Trace ---',
                    ...$scope.trace.map(traceEntryText)
                ];
                if ($scope.result) {
                    lines.push('', '--- Result ---');
                    if ($scope.result.last_response) {
                        lines.push($scope.result.last_response.startLine);
                        lines.push(...$scope.notableHeaders($scope.result.last_response.headers).map(key => `${key}: ${$scope.result.last_response.headers[key]}`));
                    }
                    lines.push($scope.result.result || (($scope.result.success ? 'SUCCESS' : 'FAILED') + ($scope.result.hangup_cause ? ` (${$scope.result.hangup_cause})` : '')));
                }
                return lines.join('\n');
            }

            // Mirrors trunk-info.tab.js's copySipServerUri.
            $scope.copyTrace = async function (event) {
                if (event) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                const text = buildTraceText();

                try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(text);
                    } else {
                        const textarea = document.createElement('textarea');

                        textarea.value = text;
                        textarea.setAttribute('readonly', '');
                        textarea.style.position = 'absolute';
                        textarea.style.left = '-9999px';

                        document.body.appendChild(textarea);
                        textarea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textarea);
                    }

                    $scope.copiedTrace = true;
                    safeApply();

                    setTimeout(function () {
                        $scope.copiedTrace = false;
                        safeApply();
                    }, 1500);
                } catch (error) {
                    console.log('Debug trace copy error:', error);
                }
            };

            // Terse one-line summary of a terminal 'result' event, pushed
            // into the trace pane itself (see handleStreamEvent below) -
            // $scope.result alone is never rendered anywhere in this
            // template, so without this a real failure (e.g. a register
            // test whose gateway never reached REGED) looked indistinguishable
            // from the request having silently done nothing at all -
            // confirmed live 2026-08-16.
            function resultSummaryText(event) {
                const label = event.success ? 'SUCCESS' : 'FAILED';
                const headline = event.last_response ? ` - ${event.last_response.startLine}` : '';
                const detail = event.result ? event.result.trim() : '';
                return `Result: ${label}${headline}${detail ? '\n' + detail : ''}`;
            }

            // Shared by startTest and startRegisterTest below - both stream
            // the same NDJSON event shapes (see SipTrunksService.testCall's
            // own comment) into the one trace/result pane.
            function handleStreamEvent(event) {
                if (event.type === 'sip' || event.type === 'log') {
                    $scope.trace.push(event);
                } else if (event.type === 'result') {
                    $scope.result = event;
                    $scope.trace.push({ type: 'log', text: resultSummaryText(event) });
                } else if (event.type === 'error') {
                    $scope.result = { success: false, result: event.message, hangup_cause: null, last_response: null };
                    $scope.trace.push({ type: 'log', text: `Error: ${event.message}` });
                }
                safeApply();
            }

            $scope.startTest = async function () {
                const to = ($scope.to || '').trim();
                const from = ($scope.from || '').trim();
                if (!to || !$scope.trunk.enabled) return;

                $scope.lastTestType = 'call';
                $scope.testing = true;
                $scope.trace = [];
                $scope.result = null;

                try {
                    await SipTrunksService.testCall($scope.trunk.trunk_id, { from, to }, handleStreamEvent);
                } catch (error) {
                    console.log('Test call error:', error);
                    showError(error.message || 'Failed to start the test call');
                } finally {
                    $scope.testing = false;
                    safeApply();
                }
            };

            // Forces a fresh REGISTER attempt on the trunk's gateway - only
            // meaningful for a trunk with register: true (the server 400s
            // otherwise, see sip-gudhub-server's routes.js); the button is
            // disabled in that case too (see the template), this guard is
            // just belt-and-suspenders against a stale disabled state.
            $scope.startRegisterTest = async function () {
                if (!$scope.trunk.enabled || !$scope.trunk.register) return;

                $scope.lastTestType = 'register';
                $scope.testing = true;
                $scope.trace = [];
                $scope.result = null;

                try {
                    await SipTrunksService.testRegister($scope.trunk.trunk_id, handleStreamEvent);
                } catch (error) {
                    console.log('Test register error:', error);
                    showError(error.message || 'Failed to start the register test');
                } finally {
                    $scope.testing = false;
                    safeApply();
                }
            };
        }],
        templateUrl: trunkDebugTemplateUrl
    };
}]);
